import * as React from 'react';
import { observer } from 'mobx-react';
import * as uuid from 'uuid';
import { runInAction } from 'mobx';
import InputWrapper, { InputType } from '../Inputs/InputWrapper';
import { DisplayType } from '../Models/Enums';
import InputsHelper from '../Helpers/InputsHelper';
import { BoundProp, getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

export interface ITextAreaProps<T> {
	model: T;
	modelProperty: BoundProp<T, string>;
	id?: string;
	name?: string;
	className?: string;
	displayType?: DisplayType;
	label?: React.ReactNode;
	labelVisible?: boolean;
	isRequired?: boolean;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	staticInput?: boolean;
	tooltip?: string;
	subDescription?: string;
	textAreaProps?: React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;
	placeholder?: string;
	errors?: string | string[];
	onAfterChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onChangeAndBlur?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const TextArea = observer(
	class TextArea<T> extends React.Component<ITextAreaProps<T>> {
		private uuid = uuid.v4();
		public textAreaInput = React.createRef<HTMLTextAreaElement>();
		private valueWhenFocused: string = '';

		constructor(props: ITextAreaProps<T>) {
			super(props);
		}

		public render() {
			const {
				model,
				modelProperty,
				textAreaProps,
				placeholder,
				name,
				className,
				displayType,
				label,
				labelVisible = true,
				id = this.uuid,
				isRequired,
				isDisabled,
				isReadOnly,
				staticInput,
				tooltip,
				subDescription,
				errors,
			} = this.props;

			const fieldId = `${id}-field`;
			const ariaLabel = InputsHelper.getAriaLabel(label, labelVisible);
			const ariaDescribedBy = InputsHelper.getAriaDescribedBy(id, tooltip, subDescription);

			return (
				<InputWrapper
					inputType={InputType.TEXTAREA}
					id={id}
					inputId={fieldId}
					className={className}
					staticInput={staticInput}
					displayType={displayType}
					isRequired={isRequired}
					tooltip={tooltip}
					subDescription={subDescription}
					label={label}
					labelVisible={labelVisible}
					errors={errors}
				>
					<textarea
						name={name}
						id={fieldId}
						value={getBoundValue(model, modelProperty) || ''}
						onChange={this.onChange}
						onBlur={this.onBlur}
						onFocus={this.onFocus}
						placeholder={placeholder}
						disabled={isDisabled}
						readOnly={staticInput || isReadOnly}
						aria-label={ariaLabel}
						aria-describedby={ariaDescribedBy}
						ref={this.textAreaInput}
						{...textAreaProps}
					/>
				</InputWrapper>
			);
		}

		public focus = () => {
			if (this.textAreaInput.current) {
				this.textAreaInput.current.focus();
			}
		};

		private onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (this.props.textAreaProps && this.props.textAreaProps.onChange) {
				return this.props.textAreaProps.onChange(event);
			}
			runInAction(() => {
				setBoundValue(this.props.model, this.props.modelProperty, event.target.value);
			});

			if (this.props.onAfterChange) {
				this.props.onAfterChange(event);
			}
		};

		private onBlur = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (this.valueWhenFocused !== event.target.value && this.props.onChangeAndBlur) {
				this.props.onChangeAndBlur(event);
			}
		};

		private onFocus = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
			this.valueWhenFocused = event.target.value;
		};
	},
);
